import React, { useEffect, useCallback, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Typography,
  Input,
  Box,
  FormControl,
  Divider,
  FormHelperText,
} from "@mui/joy";
import { isValidPhoneNumber, AsYouType } from "libphonenumber-js";
import InfoOutlined from "@mui/icons-material/InfoOutlined";

import { useToast } from "../../context/ToastContext";
import { useModal } from "../../context/ModalContext";

import { supabase } from "../../supabaseClient";

const parentGuardianSchema = yup.object().shape({
  first_name: yup.string().required("First name is required"),
  last_name: yup.string().required("Last name is required"),
  email_address: yup
    .string()
    .required("Email address is required")
    .email("Email address must be valid"),
  phone_number: yup.string().required("Phone number is required"),
});

interface ParentGuardianFormValues {
  first_name: string;
  last_name: string;
  email_address: string;
  phone_number: string;
}

interface AddEditParentGuardianModalProps {
  parentGuardian?: {
    id: string;
    first_name: string;
    last_name: string;
    email_address: string;
    phone_number: string;
  };
}

const AddEditParentGuardianModal: React.FC<AddEditParentGuardianModalProps> = ({
  parentGuardian,
}) => {
  const { closeModal, setModalTitle, registerSubmitCallback } = useModal();
  const { showToast } = useToast();

  const [errors, setErrors] = useState<{ email_address: string; phone_number: string }>({
    email_address: "",
    phone_number: "",
  });

  const defaultValues: ParentGuardianFormValues = {
    first_name: parentGuardian?.first_name || "",
    last_name: parentGuardian?.last_name || "",
    email_address: parentGuardian?.email_address || "",
    phone_number: parentGuardian?.phone_number || "",
  };

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors: formErrors },
  } = useForm<ParentGuardianFormValues>({
    defaultValues,
    resolver: yupResolver(parentGuardianSchema),
    mode: "onChange",
  });

  const validateEmailAddress = (value: string) => {
    const isValid = /\S+@\S+\.\S+/.test(value);

    setErrors((prev) => ({
      ...prev,
      email_address: isValid ? "" : "Invalid email address",
    }));
  };

  const validateAndFormatPhoneNumber = (value: string) => {
    const formatter = new AsYouType("US");
    const formattedNumber = formatter.input(value);

    const isValid = isValidPhoneNumber(formattedNumber, "US");

    setErrors((prev) => ({
      ...prev,
      phone_number: isValid ? "" : "Invalid phone number",
    }));

    setValue("phone_number", formattedNumber, { shouldValidate: true });
  };

  const onSubmit = useCallback(
    async (data: ParentGuardianFormValues) => {
      try {
        if (parentGuardian?.id) {
          // Update existing parent/guardian
          const { error } = await supabase
            .from("parent_guardians")
            .update(data)
            .eq("id", parentGuardian.id);

          if (error) {
            throw error;
          }
        } else {
          // Insert new parent/guardian
          const { error } = await supabase.from("parent_guardians").insert([data]);

          if (error) {
            throw error;
          }
        }

        showToast("Parent / Guardian saved successfully!", "success");
        closeModal();
      } catch (error: any) {
        console.error("Error saving parent / guardian:", error);
        showToast(`Error saving parent / guardian. ${error.message}`, "danger");
      }
    },
    [parentGuardian, showToast, closeModal]
  );

  useEffect(() => {
    setModalTitle(parentGuardian ? "Edit Parent / Guardian" : "Add Parent / Guardian");
    registerSubmitCallback(() => handleSubmit(onSubmit)());
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  return (
    <Box sx={{ height: "325px", position: "relative" }}>
      <form>
        <Box display="flex" gap={2} sx={{ flexWrap: "wrap", width: "100%" }}>
          <FormControl sx={{ flex: 1, minWidth: "200px" }}>
            <Typography component="label" htmlFor="first_name">
              First Name
            </Typography>
            <Controller
              control={control}
              name="first_name"
              render={({ field }) => (
                <Input
                  {...field}
                  id="first_name"
                  placeholder="First Name"
                  error={!!formErrors.first_name}
                />
              )}
            />
            <Box sx={{ minHeight: "24px" }}>
              {formErrors.first_name && (
                <Typography color="danger">{formErrors.first_name.message}</Typography>
              )}
            </Box>
          </FormControl>

          <FormControl sx={{ flex: 1, minWidth: "200px" }}>
            <Typography component="label" htmlFor="last_name">
              Last Name
            </Typography>
            <Controller
              control={control}
              name="last_name"
              render={({ field }) => (
                <Input
                  {...field}
                  id="last_name"
                  placeholder="Last Name"
                  error={!!formErrors.last_name}
                />
              )}
            />
            <Box sx={{ minHeight: "24px" }}>
              {formErrors.last_name && (
                <Typography color="danger">{formErrors.last_name.message}</Typography>
              )}
            </Box>
          </FormControl>
        </Box>

        <Divider sx={{ my: 2 }} />

        <FormControl sx={{ flex: 1, minWidth: "100%", mt: 2 }} error={Boolean(errors.email_address)}>
          <Typography component="label" htmlFor="email_address">
            Email Address
          </Typography>
          <Controller
            control={control}
            name="email_address"
            render={({ field }) => (
              <Input
                {...field}
                id="email_address"
                placeholder="Email Address"
                onChange={(e) => {
                  field.onChange(e);
                  validateEmailAddress(e.target.value);
                }}
                error={!!formErrors.email_address || !!errors.email_address}
              />
            )}
          />
          <Box sx={{ minHeight: "26px" }}>
            {(formErrors.email_address || errors.email_address) && (
              <FormHelperText>
                <InfoOutlined /> {formErrors.email_address?.message || errors.email_address}
              </FormHelperText>
            )}
          </Box>
        </FormControl>

        <FormControl sx={{ flex: 1, minWidth: "100%" }} error={Boolean(errors.phone_number)}>
          <Typography component="label" htmlFor="phone_number">
            Phone Number
          </Typography>
          <Controller
            control={control}
            name="phone_number"
            render={({ field }) => (
              <Input
                {...field}
                id="phone_number"
                placeholder="Phone Number"
                onChange={(e) => {
                  field.onChange(e);
                  validateAndFormatPhoneNumber(e.target.value);
                }}
                error={!!formErrors.phone_number || !!errors.phone_number}
              />
            )}
          />
          <Box sx={{ minHeight: "26px" }}>
            {(formErrors.phone_number || errors.phone_number) && (
              <FormHelperText>
                <InfoOutlined /> {formErrors.phone_number?.message || errors.phone_number}
              </FormHelperText>
            )}
          </Box>
        </FormControl>
      </form>
    </Box>
  );
};

export default AddEditParentGuardianModal;
